// competition-formats.js — Tab 5: Format library with inline caption editing.
// Extracts formats panel from competition.js; adds inline edit via PATCH.

import { apiFetch } from './utils.js';

export async function renderFormatsTab() {
  return `
    <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:1rem">
      <span style="font-size:.9rem;font-weight:600;color:var(--text-primary)">Format Library</span>
      <button class="btn-add-inline" onclick="window.openAddFormatModal()">+ New Format</button>
    </div>
    <div id="formats-list"><div class="loading-state" style="font-size:.82rem">Loading…</div></div>
    ${renderAddFormatModal()}
  `;
}

export async function loadFormatsTab() {
  const el = document.getElementById('formats-list');
  if (!el) return;
  try {
    const formats = await apiFetch('/api/competition/formats');
    if (!formats.length) {
      el.innerHTML = '<div class="empty-state" style="font-size:.82rem">No formats defined yet.</div>';
      return;
    }
    el.innerHTML = formats.map(f => `
      <div class="format-row" id="fmt-row-${f.id}">
        <div class="format-row-header" onclick="window.toggleFormatDetail(${f.id})">
          <span class="fmt-circuit-badge">${f.circuit}</span>
          <span class="fmt-name">${f.format_name}</span>
          ${f.auto_detected ? '<span class="fmt-auto-badge">auto</span>' : ''}
          <span class="fmt-season">${f.season_start || '?'}${f.season_end ? '–' + f.season_end : '+'}</span>
          <span class="fmt-cap-count">${f.caption_count} captions</span>
          <span class="fmt-expand-icon" id="fmt-expand-${f.id}">▶</span>
        </div>
        <div class="format-captions-detail hidden" id="fmt-detail-${f.id}" style="padding:.5rem 1rem .75rem">
          <div class="fmt-detail-loading" style="font-size:.8rem;color:var(--text-muted)">Loading captions…</div>
        </div>
      </div>`).join('');
  } catch (err) {
    el.innerHTML = `<div style="color:var(--accent-pink);font-size:.82rem">Failed to load formats: ${err.message}</div>`;
  }
}

function renderAddFormatModal() {
  return `
    <div id="add-format-modal" class="modal-overlay hidden">
      <div class="modal-dialog" style="max-width:600px">
        <div class="modal-header">
          <span>New Circuit Format</span>
          <button class="modal-close-btn" onclick="document.getElementById('add-format-modal').classList.add('hidden')">×</button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-field">
              <label>Circuit</label>
              <input type="text" id="fmt-circuit" class="form-input" placeholder="CBA, TOB, MFA, UIL…">
            </div>
            <div class="form-field">
              <label>Format Name</label>
              <input type="text" id="fmt-name" class="form-input" placeholder="e.g. CBA_STANDARD">
            </div>
          </div>
          <div class="form-row">
            <div class="form-field">
              <label>Season Start</label>
              <input type="number" id="fmt-season-start" class="form-input" placeholder="2024">
            </div>
            <div class="form-field">
              <label>Season End (blank = current)</label>
              <input type="number" id="fmt-season-end" class="form-input" placeholder="leave blank if still active">
            </div>
          </div>
          <div class="form-field">
            <label>Notes</label>
            <input type="text" id="fmt-notes" class="form-input" placeholder="optional">
          </div>
          <div style="margin-top:.75rem">
            <div style="font-size:.8rem;font-weight:600;color:var(--text-muted);margin-bottom:.4rem">
              Captions (one per line: <code>raw_name | group | sub1_label | sub2_label</code>)
            </div>
            <textarea id="fmt-captions-text" class="form-input" rows="8" style="font-family:monospace;font-size:.78rem"
              placeholder="Ensemble Visual | visual | Comp | Exc
Field Music | music | Voc | Exc
Overall Effect Visual | general_effect | Rep | Perf
Color Guard | guard | Prog | Exc
Percussion | percussion | Prog | Exc"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button onclick="window.saveNewFormat()" class="btn-primary">Save Format</button>
        </div>
      </div>
    </div>`;
}

export function initFormatsHandlers() {
  window.openAddFormatModal = function() {
    document.getElementById('add-format-modal')?.classList.remove('hidden');
  };

  window.saveNewFormat = async function() {
    const circuit  = document.getElementById('fmt-circuit')?.value.trim();
    const fmtName  = document.getElementById('fmt-name')?.value.trim();
    const sStart   = document.getElementById('fmt-season-start')?.value;
    const sEnd     = document.getElementById('fmt-season-end')?.value;
    const notes    = document.getElementById('fmt-notes')?.value.trim();
    const rawCaps  = document.getElementById('fmt-captions-text')?.value;
    if (!circuit || !fmtName) { alert('Circuit and format name are required'); return; }
    const captions = (rawCaps || '').split('\n')
      .map(l => l.trim()).filter(Boolean)
      .map((l, i) => {
        const parts = l.split('|').map(p => p.trim());
        return {
          caption_raw_name: parts[0] || '', caption_group: parts[1] || 'other',
          sub_score_1_label: parts[2] || null, sub_score_2_label: parts[3] || null,
          sort_order: i + 1
        };
      }).filter(c => c.caption_raw_name);
    if (!captions.length) { alert('At least one caption line is required'); return; }
    try {
      await apiFetch('/api/competition/formats', {
        method: 'POST',
        body: JSON.stringify({
          circuit, format_name: fmtName,
          season_start: sStart ? parseInt(sStart) : null,
          season_end:   sEnd   ? parseInt(sEnd)   : null,
          auto_detected: false, notes, captions
        })
      });
      document.getElementById('add-format-modal')?.classList.add('hidden');
      await loadFormatsTab();
    } catch (err) {
      alert('Failed to save format: ' + err.message);
    }
  };

  window.toggleFormatDetail = async function(formatId) {
    const detail = document.getElementById(`fmt-detail-${formatId}`);
    const icon   = document.getElementById(`fmt-expand-${formatId}`);
    if (!detail) return;
    if (!detail.classList.contains('hidden')) {
      detail.classList.add('hidden');
      if (icon) icon.textContent = '▶';
      return;
    }
    detail.classList.remove('hidden');
    if (icon) icon.textContent = '▼';
    if (detail.dataset.loaded) return;
    detail.dataset.loaded = '1';
    try {
      const data = await apiFetch(`/api/competition/formats/${formatId}`);
      const rows = (data.captions || []).map(c => `
        <tr>
          <td style="padding:.25rem .5rem">${c.sort_order}</td>
          <td style="padding:.25rem .5rem">${c.caption_raw_name}</td>
          <td style="padding:.25rem .5rem"><span class="fmt-group-badge">${c.caption_group}</span></td>
          <td style="padding:.25rem .5rem">
            <input class="fmt-inline-input" value="${c.caption_normalized_key || ''}"
              placeholder="normalized_key"
              onblur="window.saveCapDef(${c.id}, 'caption_normalized_key', this.value)"
              style="width:130px;font-size:.76rem;padding:2px 4px;background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.12);border-radius:3px;color:inherit">
          </td>
          <td style="padding:.25rem .5rem">
            <select class="fmt-inline-input"
              onchange="window.saveCapDef(${c.id}, 'caption_group', this.value)"
              style="font-size:.76rem;padding:2px 4px;background:rgba(30,40,55,1);border:1px solid rgba(255,255,255,.12);border-radius:3px;color:inherit">
              ${['music','visual','general_effect','guard','percussion','other'].map(g =>
                `<option value="${g}" ${g === c.caption_group ? 'selected' : ''}>${g}</option>`
              ).join('')}
            </select>
          </td>
          <td style="padding:.25rem .5rem">${[c.sub_score_1_label, c.sub_score_2_label].filter(Boolean).join(' / ') || '—'}</td>
          <td style="padding:.25rem .5rem">${c.is_auxiliary ? '<span class="fmt-aux-badge">aux</span>' : ''}</td>
        </tr>`).join('');
      detail.innerHTML = `
        <table style="width:100%;border-collapse:collapse;font-size:.78rem">
          <thead><tr style="color:#5a7a9a">
            <th style="padding:.25rem .5rem;text-align:left">#</th>
            <th style="padding:.25rem .5rem;text-align:left">Caption</th>
            <th style="padding:.25rem .5rem;text-align:left">Group</th>
            <th style="padding:.25rem .5rem;text-align:left">Normalized Key</th>
            <th style="padding:.25rem .5rem;text-align:left">Group Edit</th>
            <th style="padding:.25rem .5rem;text-align:left">Sub-labels</th>
            <th style="padding:.25rem .5rem;text-align:left"></th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>`;
    } catch (err) {
      detail.innerHTML = `<div style="color:var(--accent-pink);font-size:.78rem">Failed to load: ${err.message}</div>`;
    }
  };

  window.saveCapDef = async function(defId, field, value) {
    try {
      await apiFetch(`/api/competition/caption-definitions/${defId}`, {
        method: 'PATCH',
        body: JSON.stringify({ [field]: value })
      });
    } catch (err) {
      alert('Failed to save: ' + err.message);
    }
  };
}
